/**
 * Outreach TanStack Query hooks — data fetching
 *
 * Replaces manual useState/useCallback/useEffect fetching in state hooks.
 * Follows the same pattern as useKanbanLeads / useLeadDetail.
 *
 * Phase 97.8 W2: useInstantlyCampaignsQuery + useInstantlyAccountsQuery removed —
 * underlying API helpers + backend routes were deleted in Wave 1.
 */
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { getCampaigns, getCampaignDetails } from '../../api/outreach/campaigns';
import {
  getContactLists, getContactListDetails
} from '../../api/outreach/contactLists';
import { getTemplates } from '../../api/outreach/templates';

const STALE_1_MIN = 60_000;

// ── Campaigns ────────────────────────────────────────────────

/**
 * Query: campaign list with metadata (userRole, teams, managers)
 * @param {{ owner?: string, teamId?: string|null, managerId?: string|null }} filters
 */
export function useCampaignsQuery(filters = {}) {
  return useQuery({
    queryKey: queryKeys.outreach.campaigns.list(filters),
    queryFn: () => getCampaigns(filters),
    staleTime: STALE_1_MIN,
  });
}

/**
 * Query: single campaign details (campaign + recipients)
 * @param {string|null} campaignId
 */
export function useCampaignDetailQuery(campaignId) {
  return useQuery({
    queryKey: queryKeys.outreach.campaigns.detail(campaignId),
    queryFn: () => getCampaignDetails(campaignId),
    staleTime: STALE_1_MIN,
    enabled: Boolean(campaignId),
  });
}

// ── Contact Lists ────────────────────────────────────────────

/**
 * Query: contact lists with metadata
 * @param {{ owner?: string, teamId?: string|null, managerId?: string|null }} filters
 */
export function useContactListsQuery(filters = {}) {
  return useQuery({
    queryKey: queryKeys.outreach.contactLists.list(filters),
    queryFn: () => getContactLists(filters),
    staleTime: STALE_1_MIN,
  });
}

/**
 * Query: single contact list details (with contacts)
 * @param {string|null} listId
 */
export function useContactListDetailQuery(listId) {
  return useQuery({
    queryKey: queryKeys.outreach.contactLists.detail(listId),
    queryFn: () => getContactListDetails(listId),
    staleTime: STALE_1_MIN,
    enabled: Boolean(listId),
  });
}

// ── Templates ────────────────────────────────────────────────

/**
 * Query: all templates
 */
export function useTemplatesQuery() {
  return useQuery({
    queryKey: queryKeys.outreach.templates.list(),
    queryFn: getTemplates,
    staleTime: STALE_1_MIN,
  });
}
